import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { formatToday } from '@/utils/dataUtils';
import { WpPeriodTabsBar } from '@/adjustment/hooks/WpPeriodTabsUi';
import { useWpSignoffScope } from '@/adjustment/hooks/WpSignoffScopeContext';
import { useWpSignoffStore } from '@/store/audit/wpSignoffStore';
import { queryUsersByDeptIds } from '@/services/manageService/userService';
import type { UserInfo } from '@/store/admin/userInfo';
import userStore from '@/store/admin/userStore';
import { DatePicker } from '@/pages/components/common/DatePicker';
import { UserMultiSelect } from '@/pages/components/common/UserMultiSelect';

export type WpHeaderProps = {
  itemName?: string;
  workingName?: string;
  periodFrom?: string | null;
  periodTo?: string | null;
  preparedBy?: string | null;
  reviewedBy?: string | null;
  preparedDate?: string | null;
  reviewedDate?: string | null;
  dateText?: string;
  hidePeriodTabs?: boolean;
};

type UserOption = { userId: number; label: string };

type SignoffKey = {
  companyId: number;
  annualId: number;
  code: string;
  yearOfAssessment: string;
};

function displayName(user: UserInfo): string {
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  // WHY: never show numeric userId in Prepared/Reviewed chips
  return name || user.username || user.chineseName || '-';
}

/** Split backend joined display names (顿号 / comma; tolerate corrupted "?" join). */
function splitJoinedNames(joined: string | null | undefined): string[] {
  if (!joined?.trim()) return [];
  return joined.split(/[、,，?]/).map((part) => part.trim()).filter((part) => part.length > 0);
}

/**
 * Ensure selected userIds have name labels for UserMultiSelect.
 * Dept options first; then hydrate preparedBy/reviewedBy; then current login user.
 */
function mergeOptionsWithSelected(
  base: UserOption[],
  selectedIds: number[],
  joinedNames: string | null | undefined,
  fallbackUser?: UserInfo | null,
): UserOption[] {
  const map = new Map<number, UserOption>();
  for (const option of base) map.set(option.userId, option);
  if (fallbackUser && Number(fallbackUser.userId) > 0) {
    const id = Number(fallbackUser.userId);
    if (!map.has(id)) {
      map.set(id, { userId: id, label: displayName(fallbackUser) });
    }
  }
  const names = splitJoinedNames(joinedNames);
  selectedIds.forEach((id, index) => {
    if (map.has(id)) return;
    const label = names[index];
    if (label && label !== '-') {
      map.set(id, { userId: id, label });
    }
  });
  return Array.from(map.values());
}

function isoToDdMmYyyy(iso: string | null | undefined): string {
  if (!iso) return '-';
  const matched = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!matched) return iso;
  return `${matched[3]}/${matched[2]}/${matched[1]}`;
}

function ddMmYyyyToIso(value: string | null): string | null {
  if (!value) return null;
  const matched = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!matched) return value;
  return `${matched[3]}-${matched[2]}-${matched[1]}`;
}

/**
 * Align with RepaymentScheduleDrawer Table size="small" split rows:
 * display row ≈ 33.02px; compact control = 28px → line py ≈ 2.51px each side.
 * Rows abut (no inter-row gap) like TableBody.
 */
const SIGNOFF_ROW_H = 33.02;
const SIGNOFF_CONTROL_H = 28;

const SIGNOFF_INPUT_ROOT_SX = {
  minHeight: `${SIGNOFF_CONTROL_H}px !important`,
  boxSizing: 'border-box' as const,
  paddingTop: '2px',
  paddingBottom: '2px',
  alignItems: 'center',
};

const SIGNOFF_INPUT_FIELD_SX = {
  paddingTop: '2px !important',
  paddingBottom: '2px !important',
  lineHeight: 1.15,
};

/** Kill Autocomplete tag margin that otherwise blows past 28px. */
const SIGNOFF_CHIP_SX = {
  height: 22,
  maxHeight: 22,
  margin: '0 2px !important',
  '& .MuiChip-label': {px: 0.5, lineHeight: 1.2},
};

/** UserMultiSelect: 28px single-line; may grow only when chips wrap to next line. */
const SIGNOFF_COMPACT_CONTROL_SX = {
  m: 0,
  my: 0,
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall, & .MuiInputBase-root.MuiInputBase-sizeSmall': SIGNOFF_INPUT_ROOT_SX,
  '& .MuiInputBase-input': SIGNOFF_INPUT_FIELD_SX,
  '& .MuiAutocomplete-tag, & .MuiChip-root': SIGNOFF_CHIP_SX,
} as const;

/** DatePicker wrap: lock single-line control to 28px. */
const SIGNOFF_DATE_WRAP_SX = {
  width: 148,
  m: 0,
  my: 0,
  '& .MuiAutocomplete-root, & .MuiTextField-root, & .MuiFormControl-root': {m: 0, my: 0},
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall, & .MuiInputBase-root.MuiInputBase-sizeSmall': {
    ...SIGNOFF_INPUT_ROOT_SX,
    height: `${SIGNOFF_CONTROL_H}px !important`,
    maxHeight: `${SIGNOFF_CONTROL_H}px !important`,
  },
  '& .MuiInputBase-input': SIGNOFF_INPUT_FIELD_SX,
} as const;

function SignoffLine({
  title,
  dateIso,
  canEdit,
  options,
  selectedIds,
  lockedIds,
  onUsersChange,
  onDateChange,
}: {
  title: string;
  dateIso: string;
  canEdit: boolean;
  options: UserOption[];
  selectedIds: number[];
  lockedIds?: Set<number>;
  onUsersChange: (ids: number[]) => void;
  onDateChange: (iso: string | null) => void;
}) {
  const dateValue = isoToDdMmYyyy(dateIso);
  const pickerValue = dateValue === '-' ? null : dateValue;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        py: '2.51px',
        boxSizing: 'border-box',
        // Lock to table-row height so Prepared/Reviewed abut like TableBody rows.
        minHeight: SIGNOFF_ROW_H,
        height: SIGNOFF_ROW_H,
        my: 0,
        overflow: 'visible',
        '& .MuiAutocomplete-root, & .MuiTextField-root, & .MuiFormControl-root': {
          m: 0,
          my: 0,
        },
      }}
    >
      <Typography variant="body2" sx={{whiteSpace: 'nowrap', lineHeight: 1.15, fontSize: '0.75rem'}}>
        {title}:
      </Typography>
      <Box sx={{width: 220, maxWidth: 220, minWidth: 0, flexShrink: 0, overflow: 'hidden'}}>
        <UserMultiSelect
          options={options}
          value={selectedIds}
          onChange={onUsersChange}
          disabled={!canEdit}
          lockedIds={lockedIds}
          fullWidth
          compact
          sx={SIGNOFF_COMPACT_CONTROL_SX}
        />
      </Box>
      <Typography variant="body2" sx={{whiteSpace: 'nowrap', lineHeight: 1.15, fontSize: '0.75rem'}}>
        Date:
      </Typography>
      <Box sx={{...SIGNOFF_DATE_WRAP_SX, pointerEvents: canEdit ? 'auto' : 'none'}}>
        <DatePicker
          display={false}
          fullWidth
          compact
          align="right"
          value={pickerValue}
          onChange={(next) => onDateChange(ddMmYyyyToIso(next))}
        />
      </Box>
    </Box>
  );
}

export function WpHeader({
  itemName,
  workingName,
  periodFrom,
  periodTo,
  preparedDate,
  reviewedDate,
  dateText,
  hidePeriodTabs = false,
}: WpHeaderProps) {
  const scope = useWpSignoffScope();
  const keyParts = useMemo<SignoffKey>(() => ({
    companyId: scope?.companyId || 0,
    annualId: scope?.annualId || 0,
    code: scope?.code || '',
    yearOfAssessment: scope?.yearOfAssessment || '',
  }), [scope?.annualId, scope?.code, scope?.companyId, scope?.yearOfAssessment]);

  const storeKey = keyParts.companyId && keyParts.annualId && keyParts.code
    ? `${keyParts.companyId}:${keyParts.annualId}:${keyParts.code.toUpperCase()}:${keyParts.yearOfAssessment}`
    : '';
  const signoff = useWpSignoffStore((state) => (storeKey ? state.byKey[storeKey] : undefined));
  const locked = useWpSignoffStore((state) => (storeKey ? state.lockedPreparedByKey[storeKey] : undefined));

  const [options, setOptions] = useState<UserOption[]>([]);
  const readOnly = !!scope?.readOnly;

  useEffect(() => {
    let mounted = true;
    queryUsersByDeptIds(2001)
      .then((users) => {
        if (!mounted) return;
        const boxed = users as unknown as { data?: UserInfo[] } | UserInfo[];
        const list = Array.isArray(boxed)
          ? boxed
          : (Array.isArray(boxed?.data) ? boxed.data : []);
        setOptions(list
          .filter((user) => user && Number(user.userId) > 0)
          .map((user) => ({ userId: Number(user.userId), label: displayName(user) })));
      })
      .catch(() => {
        if (mounted) setOptions([]);
      });
    return () => { mounted = false; };
  }, []);

  const preparedIds = signoff?.preparedUserIds || [];
  const reviewedIds = signoff?.reviewedUserIds || [];
  const preparedDateValue = signoff?.preparedDate || preparedDate || dateText || formatToday();
  const reviewedDateValue = signoff?.reviewedDate || reviewedDate || dateText || formatToday();
  const lockedSet = useMemo(() => {
    const set = new Set(locked || []);
    const currentUserId = userStore.getCurrentUserId();
    if (currentUserId > 0) set.add(currentUserId);
    return set;
  }, [locked]);

  const preparedOptions = useMemo(
    () => mergeOptionsWithSelected(options, preparedIds, signoff?.preparedBy, userStore.user),
    [options, preparedIds, signoff?.preparedBy],
  );
  const reviewedOptions = useMemo(
    () => mergeOptionsWithSelected(options, reviewedIds, signoff?.reviewedBy, userStore.user),
    [options, reviewedIds, signoff?.reviewedBy],
  );

  const periodText = useMemo(() => {
    if (periodFrom && periodTo) return `From ${periodFrom} To ${periodTo}`;
    if (periodTo) return String(periodTo);
    return null;
  }, [periodFrom, periodTo]);

  const canEdit = !readOnly && !!keyParts.companyId && !!keyParts.annualId && !!keyParts.code;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="h6" fontWeight={800}> {itemName}</Typography>
          {!!workingName && (
            <Typography variant="subtitle1" fontWeight={600}>{workingName}</Typography>
          )}
          {!!periodText && (
            <Typography variant="subtitle2" color="text.secondary">{periodText}</Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // RepaymentScheduleDrawer TableBody: rows abut — no rowGap / margin between rows.
            gap: 0,
            rowGap: 0,
            '& > *': {my: 0},
            '& > * + *': {mt: 0},
          }}
        >
          <SignoffLine
            title="Prepared by"
            dateIso={preparedDateValue}
            canEdit={canEdit}
            options={preparedOptions}
            selectedIds={preparedIds}
            lockedIds={lockedSet}
            onUsersChange={(ids) => useWpSignoffStore.getState().setPreparedUserIds(keyParts, ids)}
            onDateChange={(iso) => useWpSignoffStore.getState().setPreparedDate(keyParts, iso)}
          />
          <SignoffLine
            title="Reviewed by"
            dateIso={reviewedDateValue}
            canEdit={canEdit}
            options={reviewedOptions}
            selectedIds={reviewedIds}
            onUsersChange={(ids) => useWpSignoffStore.getState().setReviewedUserIds(keyParts, ids)}
            onDateChange={(iso) => useWpSignoffStore.getState().setReviewedDate(keyParts, iso)}
          />
        </Box>
      </Box>
      {!hidePeriodTabs && <WpPeriodTabsBar />}
    </Box>
  );
}
